import { useEffect, useState } from 'react';
import { API_BASE } from './api-base';
import { VOICE_CALL_SPEC, toggleOverride } from './dev-feature-toggles';

/**
 * Client read of the `symphonyVoiceCallWeb` switch — an OFF switch, not an ON one. Voice is
 * enabled by default; the release only matters when it exists and is set to control. Fetched once
 * from the server route and cached for the page load, and an unreachable route leaves voice ON
 * (the switch is unavailable, not thrown — see pages/api/flags/voice-call.ts).
 *
 * A dev-tools **Feature toggles** override wins over the conducted value, so
 * forcing it On/Off exercises the real flow rather than a special case. That
 * override is inert without a dev-tools grant, so a real user always gets the
 * conducted value.
 */

let cached: Promise<boolean> | null = null;

export function voiceCallEnabled(): Promise<boolean> {
  // Checked on every call, not memoized: the override can be set mid-session
  // from the dev panel.
  const forced = toggleOverride(VOICE_CALL_SPEC);
  if (forced !== undefined) return Promise.resolve(forced === 'true');
  if (cached) return cached;
  cached = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/flags/voice-call`, { credentials: 'include' });
      if (!res.ok) return true;
      return ((await res.json()) as boolean) !== false;
    } catch {
      return true;
    }
  })();
  return cached;
}

/**
 * Resolve the switch for rendering. Starts false so the button never flashes in and then out for
 * a user the switch has turned off; the enabled answer arrives a moment later.
 */
export function useVoiceCallEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void voiceCallEnabled().then((on) => {
      if (live) setEnabled(on);
    });
    return () => {
      live = false;
    };
  }, []);
  return enabled;
}
